import { Injectable, signal } from '@angular/core';
import { Observable, of, switchMap } from 'rxjs';
import { BookingService } from './booking.service';
import { WorkspaceContextService } from './workspace-context.service';
import { readApiErrorMessage } from '../utils/api-error.util';

export type PropertyBookingsBuilderOption = {
  workspaceId: string;
  workspaceName: string;
};

type ModalResolve = (granted: boolean) => void;

/**
 * Workspace selection for Property Bookings (parent sidebar menu only).
 * Submodule routes use {@link WorkspaceContextService} after connect.
 */
@Injectable({ providedIn: 'root' })
export class PropertyBookingsWorkspaceService {
  readonly modalOpen = signal(false);
  readonly builders = signal<PropertyBookingsBuilderOption[]>([]);
  readonly modalLoading = signal(false);
  readonly modalError = signal<string | null>(null);
  readonly connectingWorkspaceId = signal<string | null>(null);

  private modalResolve: ModalResolve | null = null;
  private buildersCache: PropertyBookingsBuilderOption[] | null = null;

  constructor(
    private readonly bookingService: BookingService,
    private readonly workspaceContext: WorkspaceContextService,
  ) {}

  /**
   * Run when the user expands Property Bookings from the sidebar parent control.
   * Shows the picker modal when multiple builders exist and no valid workspace is active.
   */
  prepareSectionAccess(): Observable<boolean> {
    return this.loadBuilders().pipe(
      switchMap((builders) => {
        if (!builders.length) {
          return of(true);
        }
        if (builders.length === 1) {
          return this.connectBuilder(builders[0].workspaceId).pipe(
            switchMap(() => of(true)),
          );
        }
        if (this.hasValidActiveWorkspace(builders)) {
          return of(true);
        }
        return this.openModalAndWait(builders);
      }),
    );
  }

  /** Change builder from Property Bookings pages (reopens modal). */
  requestBuilderChange(): Observable<boolean> {
    return this.loadBuilders().pipe(
      switchMap((builders) => {
        if (!builders.length) {
          return of(false);
        }
        if (builders.length === 1) {
          return this.connectBuilder(builders[0].workspaceId).pipe(
            switchMap(() => of(true)),
          );
        }
        return this.openModalAndWait(builders);
      }),
    );
  }

  /**
   * Submodule entry: auto-connect when only one builder; does not open the modal.
   */
  prepareSubmoduleAccess(): Observable<boolean> {
    return this.loadBuilders().pipe(
      switchMap((builders) => {
        if (!builders.length) {
          return of(false);
        }
        if (builders.length === 1) {
          return this.connectBuilder(builders[0].workspaceId).pipe(
            switchMap(() => of(true)),
          );
        }
        return of(this.hasValidActiveWorkspace(builders));
      }),
    );
  }

  hasValidActiveWorkspace(
    builders: PropertyBookingsBuilderOption[],
  ): boolean {
    const active = this.workspaceContext.getActiveWorkspaceId()?.trim();
    if (!active) {
      return false;
    }
    return builders.some((b) => b.workspaceId === active);
  }

  connectBuilderFromModal(workspaceId: string): void {
    const id = workspaceId?.trim();
    if (!id || this.connectingWorkspaceId()) {
      return;
    }
    this.modalError.set(null);
    this.connectingWorkspaceId.set(id);
    this.connectBuilder(id).subscribe({
      next: () => {
        this.connectingWorkspaceId.set(null);
        this.closeModal(true);
      },
      error: (err: unknown) => {
        this.connectingWorkspaceId.set(null);
        this.modalError.set(
          readApiErrorMessage(err, 'Failed to connect to builder.'),
        );
      },
    });
  }

  cancelModal(): void {
    this.closeModal(false);
  }

  private openModalAndWait(
    builders: PropertyBookingsBuilderOption[],
  ): Observable<boolean> {
    return new Observable<boolean>((subscriber) => {
      this.builders.set(builders);
      this.modalError.set(null);
      this.modalLoading.set(false);
      this.modalOpen.set(true);
      this.modalResolve = (granted) => {
        subscriber.next(granted);
        subscriber.complete();
      };
    });
  }

  private closeModal(granted: boolean): void {
    this.modalOpen.set(false);
    this.connectingWorkspaceId.set(null);
    const resolve = this.modalResolve;
    this.modalResolve = null;
    resolve?.(granted);
  }

  selectBuilderWorkspace(workspaceId: string): Observable<void> {
    return this.connectBuilder(workspaceId);
  }

  private connectBuilder(workspaceId: string): Observable<void> {
    return this.bookingService.switchBuilderWorkspace(workspaceId);
  }

  loadBuilders(): Observable<PropertyBookingsBuilderOption[]> {
    if (this.buildersCache?.length) {
      return of(this.buildersCache);
    }
    return new Observable((subscriber) => {
      this.bookingService.listAssociatedBuilders().subscribe({
        next: (builders) => {
          this.buildersCache = builders;
          subscriber.next(builders);
          subscriber.complete();
        },
        error: (err) => {
          subscriber.error(err);
        },
      });
    });
  }

  /** Call after login/logout or when workspace links may have changed. */
  invalidateBuildersCache(): void {
    this.buildersCache = null;
  }
}
